import { useState } from 'react';
import { ArrowRight, ArrowLeft, Upload, X, Loader2, Check, Plus, MessageSquare, Image, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';

const API = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '')}/api`;

const StepAssetUpload = ({ onComplete, onBack }) => {
  const { getAuthHeader } = useAuth();
  const { lang } = useLanguage();
  const [mediaFiles, setMediaFiles] = useState([]);
  const [uploading, setUploading] = useState(false);

  const PLATFORM_OPTIONS = [
    { value: 'linkedin', label: 'LinkedIn' },
    { value: 'instagram', label: 'Instagram' },
    { value: 'facebook', label: 'Facebook' },
    { value: 'twitter', label: 'X / Twitter' },
    { value: 'blog', label: 'Blog' },
    { value: 'newsletter', label: 'Newsletter' },
    { value: 'other', label: lang === 'de' ? 'Sonstiges' : 'Other' },
  ];

  const [inspirations, setInspirations] = useState([]);
  const [showAddInspiration, setShowAddInspiration] = useState(false);
  const [newPost, setNewPost] = useState({ platform: 'linkedin', text: '', what_i_like: '' });
  const [screenshotFiles, setScreenshotFiles] = useState([]);
  const [uploadingScreenshot, setUploadingScreenshot] = useState(false);

  // Brand media upload
  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (mediaFiles.length + files.length > 20) {
      toast.error(lang === 'de' ? 'Maximal 20 Dateien erlaubt' : 'Max 20 files allowed');
      return;
    }
    setUploading(true);
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        const isVid = file.type.startsWith('video/');
        const isDoc = file.type === 'application/pdf' || file.type.includes('word') || /\.(pdf|docx?)$/i.test(file.name);
        formData.append('media_type', isVid ? 'video' : (isDoc ? 'document' : 'image'));
        const res = await axios.post(`${API}/brand/upload-media`, formData, {
          headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' },
          timeout: 120000,
        });
        setMediaFiles(prev => [...prev, { ...res.data.media, name: file.name }]);
      } catch (err) {
        const detail = err?.response?.data?.detail || err?.message || (lang === 'de' ? 'Unbekannter Fehler' : 'Unknown error');
        toast.error(lang === 'de' ? `Upload fehlgeschlagen (${file.name}): ${detail}` : `Upload failed (${file.name}): ${detail}`);
      }
    }
    setUploading(false);
    e.target.value = '';
  };

  const removeFile = async (id) => {
    try {
      await axios.delete(`${API}/brand/media/${id}`, { headers: getAuthHeader() });
      setMediaFiles(prev => prev.filter(m => m.id !== id));
    } catch {
      toast.error(lang === 'de' ? 'Fehler beim Entfernen' : 'Error removing file');
    }
  };

  // Inspiration screenshot upload
  const handleScreenshotUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploadingScreenshot(true);
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('media_type', 'image');
        const res = await axios.post(`${API}/brand/upload-media`, formData, {
          headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' },
          timeout: 30000,
        });
        setScreenshotFiles(prev => [...prev, { ...res.data.media, name: file.name }]);
      } catch (err) {
        const detail = err?.response?.data?.detail || err?.message || (lang === 'de' ? 'Unbekannter Fehler' : 'Unknown error');
        toast.error(lang === 'de' ? `Screenshot-Upload fehlgeschlagen (${file.name}): ${detail}` : `Screenshot upload failed (${file.name}): ${detail}`);
      }
    }
    setUploadingScreenshot(false);
    e.target.value = '';
  };

  const removeScreenshot = async (id) => {
    try {
      await axios.delete(`${API}/brand/media/${id}`, { headers: getAuthHeader() });
      setScreenshotFiles(prev => prev.filter(s => s.id !== id));
    } catch {
      toast.error(lang === 'de' ? 'Fehler beim Entfernen' : 'Error removing file');
    }
  };

  const addInspiration = () => {
    if (!newPost.text.trim()) {
      toast.error(lang === 'de' ? 'Bitte füge einen Text ein' : 'Please add some text');
      return;
    }
    setInspirations(prev => [...prev, { ...newPost, id: Date.now() }]);
    setNewPost({ platform: 'linkedin', text: '', what_i_like: '' });
    setShowAddInspiration(false);
  };

  const removeInspiration = (id) => {
    setInspirations(prev => prev.filter(i => i.id !== id));
  };

  const handleSkip = () => onComplete({ skipped: true, mediaFiles: [], inspirations: [], screenshotFiles: [] });
  const handleContinue = () => onComplete({ skipped: false, mediaFiles, inspirations, screenshotFiles });

  const hasAnyContent = mediaFiles.length > 0 || inspirations.length > 0 || screenshotFiles.length > 0;

  return (
    <div data-testid="step-asset-upload" className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold tracking-tight text-white mb-2">
          {lang === 'de' ? 'Brand Assets & Inspirationen' : 'Brand assets & inspirations'}
        </h2>
        <p className="text-[#A0AEC0] text-sm">
          {lang === 'de'
            ? 'Lade Logos und Grafiken hoch und zeige uns Beispiel-Posts, die dir gefallen. Die KI leitet daraus deinen Stil ab.'
            : 'Upload logos and graphics and show us example posts you like. The AI uses these to derive your style.'}
        </p>
      </div>

      {/* ── Brand Media Upload ── */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Image className="w-4 h-4 text-[#A78BFA]" />
          <p className="text-white text-sm font-medium">{lang === 'de' ? 'Brand Assets' : 'Brand Assets'}</p>
          <span className="text-[#5A6C7D] text-xs">{lang === 'de' ? '(Logos, Fotos, Grafiken)' : '(Logos, photos, graphics)'}</span>
        </div>
        <label className={`block cursor-pointer ${mediaFiles.length >= 20 ? 'pointer-events-none opacity-50' : ''}`} data-testid="asset-upload-label">
          <input type="file" accept="image/jpeg,image/png,image/webp,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.pdf,.doc,.docx" multiple onChange={handleUpload} className="hidden" data-testid="asset-file-input" />
          <div className="flex flex-col items-center justify-center gap-2 py-6 border-2 border-dashed border-[#1A2B42] rounded-xl hover:border-[#A78BFA]/40 transition-colors">
            {uploading ? (
              <><Loader2 className="w-6 h-6 text-[#A78BFA] animate-spin" /><p className="text-[#A0AEC0] text-xs">{lang === 'de' ? 'Wird hochgeladen...' : 'Uploading...'}</p></>
            ) : (
              <><Upload className="w-6 h-6 text-[#5A6C7D]" /><p className="text-[#5A6C7D] text-xs">JPG, PNG, WebP, PDF, DOCX</p></>
            )}
          </div>
        </label>
        {mediaFiles.length > 0 && (
          <div className="grid grid-cols-6 gap-2">
            {mediaFiles.map((m) => (
              <div key={m.id} className="relative group">
                {m.file_url?.match(/\.(jpg|jpeg|png|webp)/i) ? (
                  <img src={m.file_url} alt={m.name} className="w-full aspect-square object-cover rounded-lg border border-[#1A2B42]" />
                ) : (
                  <div className="w-full aspect-square rounded-lg border border-[#1A2B42] bg-[#1A2B42] flex items-center justify-center">
                    <p className="text-[#5A6C7D] text-[9px] text-center px-1 truncate">{m.name}</p>
                  </div>
                )}
                <button onClick={() => removeFile(m.id)} className="absolute top-0.5 right-0.5 w-4 h-4 bg-red-500/80 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <X className="w-2.5 h-2.5 text-white" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Inspiration Posts ── */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MessageSquare className="w-4 h-4 text-[#00D9C8]" />
            <p className="text-white text-sm font-medium">{lang === 'de' ? 'Inspirationen' : 'Inspirations'}</p>
            <span className="text-[#5A6C7D] text-xs">{lang === 'de' ? '(Beispiel-Posts die dir gefallen)' : '(Example posts you like)'}</span>
          </div>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setShowAddInspiration(!showAddInspiration)}
            className="text-[#00D9C8] hover:bg-[#00D9C8]/10 h-7 text-xs"
            data-testid="add-inspiration-toggle"
          >
            {showAddInspiration ? <ChevronUp className="w-3.5 h-3.5 mr-1" /> : <Plus className="w-3.5 h-3.5 mr-1" />}
            {showAddInspiration
              ? (lang === 'de' ? 'Schliessen' : 'Close')
              : (lang === 'de' ? 'Post hinzufügen' : 'Add post')}
          </Button>
        </div>

        {/* Add inspiration form */}
        {showAddInspiration && (
          <div className="bg-[#1A2B42]/50 rounded-lg p-4 space-y-3 border border-[#1A2B42]" data-testid="inspiration-form">
            <div className="flex gap-2">
              {/* Native <select> with dark background rendered white-on-white
                  options in Chrome/Safari — appeared blank + unclickable, so
                  the fallback default "linkedin" always won. Replaced with the
                  shadcn/Radix Select which fully controls the dropdown styling. */}
              <Select
                value={newPost.platform}
                onValueChange={(val) => setNewPost(p => ({ ...p, platform: val }))}
              >
                <SelectTrigger
                  className="bg-[#0A1628] text-white text-xs border-[#1A2B42] h-9 w-full max-w-[220px]"
                  data-testid="inspiration-platform-select"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#0f1f36] border-[#1A2B42] text-white">
                  {PLATFORM_OPTIONS.map(p => (
                    <SelectItem
                      key={p.value}
                      value={p.value}
                      className="text-white focus:bg-[#1A2B42] focus:text-white"
                      data-testid={`inspiration-platform-option-${p.value}`}
                    >
                      {p.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Textarea
              value={newPost.text}
              onChange={(e) => setNewPost(p => ({ ...p, text: e.target.value }))}
              placeholder={lang === 'de' ? 'Post-Text hier einfügen...' : 'Paste post text here...'}
              className="bg-[#0A1628] border-transparent focus:border-[#00D9C8] text-white text-sm min-h-[100px] resize-none"
              data-testid="inspiration-text-input"
            />
            <Input
              value={newPost.what_i_like}
              onChange={(e) => setNewPost(p => ({ ...p, what_i_like: e.target.value }))}
              placeholder={lang === 'de' ? 'Was gefällt dir daran? (optional)' : 'What do you like about it? (optional)'}
              className="bg-[#0A1628] border-transparent focus:border-[#00D9C8] text-white text-sm"
              data-testid="inspiration-like-input"
            />
            <div className="flex gap-2">
              <Button onClick={addInspiration} size="sm" className="bg-[#00D9C8] hover:bg-[#00D9C8]/90 text-[#0A1628] text-xs h-8" data-testid="add-inspiration-btn">
                <Plus className="w-3.5 h-3.5 mr-1" /> {lang === 'de' ? 'Hinzufügen' : 'Add'}
              </Button>
            </div>
          </div>
        )}

        {/* Inspiration list */}
        {inspirations.length > 0 && (
          <div className="space-y-2">
            {inspirations.map((insp) => (
              <div key={insp.id} className="bg-[#0A1628] rounded-lg p-3 group relative" data-testid={`inspiration-item-${insp.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <span className="text-[#00D9C8] text-[10px] uppercase font-medium">{insp.platform}</span>
                    <p className="text-[#A0AEC0] text-xs mt-1 line-clamp-3">{insp.text}</p>
                    {insp.what_i_like && <p className="text-[#5A6C7D] text-[10px] mt-1 italic">{insp.what_i_like}</p>}
                  </div>
                  <button onClick={() => removeInspiration(insp.id)} className="text-[#5A6C7D] hover:text-red-400 shrink-0">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Screenshot upload for inspirations */}
        <div className="space-y-2">
          <p className="text-[#5A6C7D] text-xs">{lang === 'de' ? 'Oder lade Screenshots von Posts hoch:' : 'Or upload screenshots of posts:'}</p>
          <label className="block cursor-pointer" data-testid="screenshot-upload-label">
            <input type="file" accept="image/jpeg,image/png,image/webp" multiple onChange={handleScreenshotUpload} className="hidden" data-testid="screenshot-file-input" />
            <div className="flex items-center justify-center gap-2 py-3 border border-dashed border-[#1A2B42] rounded-lg hover:border-[#00D9C8]/40 transition-colors">
              {uploadingScreenshot ? (
                <Loader2 className="w-4 h-4 text-[#00D9C8] animate-spin" />
              ) : (
                <><Upload className="w-4 h-4 text-[#5A6C7D]" /><span className="text-[#5A6C7D] text-xs">{lang === 'de' ? 'Screenshots hochladen' : 'Upload screenshots'}</span></>
              )}
            </div>
          </label>
          {screenshotFiles.length > 0 && (
            <div className="grid grid-cols-4 gap-2">
              {screenshotFiles.map((s) => (
                <div key={s.id} className="relative group">
                  <img src={s.file_url} alt={s.name} className="w-full aspect-video object-cover rounded-lg border border-[#1A2B42]" />
                  <button onClick={() => removeScreenshot(s.id)} className="absolute top-0.5 right-0.5 w-4 h-4 bg-red-500/80 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <X className="w-2.5 h-2.5 text-white" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-3 pt-2">
        <Button variant="outline" onClick={onBack} className="border-[#1A2B42] text-[#A0AEC0]" data-testid="asset-back-btn">
          <ArrowLeft className="w-4 h-4 mr-2" /> {lang === 'de' ? 'Zurück' : 'Back'}
        </Button>
        <Button variant="ghost" onClick={handleSkip} className="text-[#5A6C7D] hover:text-white" data-testid="asset-skip-btn">
          {lang === 'de' ? 'Überspringen' : 'Skip'}
        </Button>
        <Button
          onClick={handleContinue}
          className="flex-1 h-12 bg-[#00D9C8] hover:bg-[#00D9C8]/90 text-[#0A1628] font-semibold"
          data-testid="asset-continue-btn"
        >
          {hasAnyContent ? (
            <>{lang === 'de' ? 'Weiter' : 'Continue'}<ArrowRight className="w-4 h-4 ml-2" /></>
          ) : (
            <>{lang === 'de' ? 'Ohne Inspirationen weiter' : 'Continue without inspirations'}<ArrowRight className="w-4 h-4 ml-2" /></>
          )}
        </Button>
      </div>
    </div>
  );
};

export default StepAssetUpload;
